
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, X } from "lucide-react";

interface MatchingCandidatesFiltersProps {
  minMatchScore: number;
  onMinMatchScoreChange: (score: number) => void;
  filters: {
    experienceLevel: string[];
    location: string[];
    skills: string[];
    availability: string;
    salaryRange: [number, number];
  };
  onFiltersChange: (filters: any) => void;
  onClearFilters: () => void;
}

export const MatchingCandidatesFilters: React.FC<MatchingCandidatesFiltersProps> = ({
  minMatchScore,
  onMinMatchScoreChange,
  filters,
  onFiltersChange,
  onClearFilters
}) => {
  const hasActiveFilters = 
    filters.experienceLevel.length > 0 ||
    filters.location.length > 0 ||
    filters.skills.length > 0 ||
    filters.availability !== 'any' ||
    filters.salaryRange[0] > 0 ||
    filters.salaryRange[1] < 200000;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Advanced Filters
          </CardTitle>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Match Score Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Minimum Match Score</Label>
            <Badge variant="outline">{minMatchScore}%</Badge>
          </div>
          <Slider
            value={[minMatchScore]}
            onValueChange={(value) => onMinMatchScoreChange(value[0])}
            max={100}
            min={0}
            step={5}
            className="w-full"
          />
        </div>

        {/* Experience Level */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Experience Level</Label>
          <div className="space-y-2">
            {['Entry Level', 'Mid Level', 'Senior Level', 'Executive'].map((level) => (
              <div key={level} className="flex items-center space-x-2">
                <Checkbox
                  id={level}
                  checked={filters.experienceLevel.includes(level)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onFiltersChange({
                        ...filters,
                        experienceLevel: [...filters.experienceLevel, level]
                      });
                    } else {
                      onFiltersChange({
                        ...filters,
                        experienceLevel: filters.experienceLevel.filter(l => l !== level)
                      });
                    }
                  }}
                />
                <Label htmlFor={level} className="text-sm">{level}</Label>
              </div>
            ))}
          </div>
        </div>

        {/* Location */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Location</Label>
          <Select
            onValueChange={(value) => {
              if (value && !filters.location.includes(value)) {
                onFiltersChange({
                  ...filters,
                  location: [...filters.location, value]
                });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Add location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Remote">Remote</SelectItem>
              <SelectItem value="New York, NY">New York, NY</SelectItem>
              <SelectItem value="San Francisco, CA">San Francisco, CA</SelectItem>
              <SelectItem value="Los Angeles, CA">Los Angeles, CA</SelectItem>
              <SelectItem value="Chicago, IL">Chicago, IL</SelectItem>
              <SelectItem value="Boston, MA">Boston, MA</SelectItem>
            </SelectContent>
          </Select>
          {filters.location.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {filters.location.map((loc) => (
                <Badge key={loc} variant="secondary" className="text-xs">
                  {loc}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onFiltersChange({
                      ...filters,
                      location: filters.location.filter(l => l !== loc)
                    })}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Availability */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Availability</Label>
          <Select
            value={filters.availability}
            onValueChange={(value) => onFiltersChange({ ...filters, availability: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="immediate">Immediate</SelectItem>
              <SelectItem value="2-weeks">2 Weeks Notice</SelectItem>
              <SelectItem value="1-month">1 Month</SelectItem>
              <SelectItem value="negotiable">Negotiable</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Salary Range */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Salary Range: ${filters.salaryRange[0].toLocaleString()} - ${filters.salaryRange[1].toLocaleString()}
          </Label>
          <div className="px-2">
            <Slider
              value={filters.salaryRange}
              onValueChange={(value) => onFiltersChange({ 
                ...filters, 
                salaryRange: value as [number, number] 
              })}
              max={200000}
              min={0}
              step={5000}
              className="w-full"
            />
          </div>
        </div>

        {/* Skills Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Required Skills</Label>
          <Input
            placeholder="Type skill and press Enter"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const skill = e.currentTarget.value.trim();
                if (skill && !filters.skills.includes(skill)) {
                  onFiltersChange({
                    ...filters,
                    skills: [...filters.skills, skill]
                  });
                  e.currentTarget.value = '';
                }
              }
            }}
          />
          {filters.skills.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {filters.skills.map((skill) => (
                <Badge key={skill} variant="secondary" className="text-xs">
                  {skill}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onFiltersChange({
                      ...filters,
                      skills: filters.skills.filter(s => s !== skill)
                    })}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
